import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Moon, Sun, Apple, Users, FolderPlus,
  ChevronLeft, ChevronRight, Mail, PlaySquare, Sparkles, Brain,
  BarChart3, ArrowRight, Menu, X, CheckCircle2, Bot,
  Building2, MessageSquare, Languages, Globe, UserCog, CalendarClock
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from './ui/button'

/* ---------- Motion presets ---------- */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

const viewportOnce = { once: true, margin: '-80px' }

/* ---------- Shared section heading ---------- */
function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      className="text-center max-w-3xl mx-auto mb-16"
    >
      {eyebrow && (
        <motion.span
          variants={fadeUp}
          className="border-beam inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-brand-700/10 dark:bg-brand-500/15 text-brand-700 dark:text-brand-300 mb-4"
        >
          <Sparkles className="w-4 h-4" />
          {eyebrow}
        </motion.span>
      )}
      <motion.h2
        variants={fadeUp}
        className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-brand-800 dark:text-brand-100 mb-4"
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p variants={fadeUp} className="text-lg text-brand-700/80 dark:text-brand-300/90">
          {subtitle}
        </motion.p>
      )}
    </motion.div>
  )
}

export default function LandingPage() {
  const [darkMode, setDarkMode] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [paused, setPaused] = useState(false)
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)

  const steps = useMemo(() => [
    {
      title: 'Register Your Company',
      description: 'Create your company account in minutes and set up secure billing with Stripe.',
      image: '/images/step-register.png',
      icon: <Building2 className="w-5 h-5" />,
    },
    {
      title: 'Add Your Employees',
      description: 'Invite by email — login credentials are sent automatically. Set job titles, working hours and absences.',
      image: '/images/step-add-employee.png',
      icon: <Users className="w-5 h-5" />,
    },
    {
      title: 'Create a Project',
      description: 'Define the scope, set the timeline and assign your project team.',
      image: '/images/step-project.png',
      icon: <FolderPlus className="w-5 h-5" />,
    },
    {
      title: 'Add a Task — AI Suggests',
      description: 'The AI instantly proposes the best person for the job, a realistic hour estimate and a start date.',
      image: '/images/step-ai-task.png',
      icon: <Bot className="w-5 h-5" />,
    },
    {
      title: 'Review & Confirm',
      description: "Read the AI's reasoning, adjust anything you like and confirm. Booked hours flow straight into utilization.",
      image: '/images/step-ai-reasoning.png',
      icon: <CheckCircle2 className="w-5 h-5" />,
    },
  ], [])

  /* Dark mode */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
  }, [darkMode])

  /* Lock body scroll while the mobile drawer is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  /* Header shadow on scroll */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /* Auto-rotate showcase */
  useEffect(() => {
    if (paused) return
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [paused, steps.length])

  const handlePrevStep = useCallback(() => {
    setActiveStep((prev) => (prev - 1 + steps.length) % steps.length)
    setPaused(true)
  }, [steps.length])

  const handleNextStep = useCallback(() => {
    setActiveStep((prev) => (prev + 1) % steps.length)
    setPaused(true)
  }, [steps.length])

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX)
  const handleTouchMove = (e) => setTouchEnd(e.touches[0].clientX)
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    if (distance > 50) handleNextStep()
    if (distance < -50) handlePrevStep()
    setTouchStart(null)
    setTouchEnd(null)
  }

  const navLinks = [
    { href: '#features', label: 'Features' },
    { href: '#intelligence', label: 'AI Intelligence' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#pricing', label: 'Pricing' },
    { href: '#download', label: 'Download' },
  ]

  const features = [
    {
      icon: <Bot className="w-6 h-6" />,
      title: 'AI Task Assignment',
      description: 'Every new task is matched to the best-suited teammate based on skills, role and real-time availability — automatically.',
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: 'AI Estimates with Reasoning',
      description: 'Get realistic hour estimates and suggested start dates, each backed by transparent reasoning. Approve or adjust with one tap.',
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Utilization & Capacity',
      description: 'Working hours, absences and booked task hours per employee — see free capacity before you plan, not after.',
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'Chat & Photos in Every Task',
      description: 'Discuss work where it happens — comment and chat inside the task, message the manager directly and attach photos for context.',
    },
    {
      icon: <Languages className="w-6 h-6" />,
      title: 'Multilingual by Design',
      description: 'Every user works in their own preferred language. Assign tasks to anyone — the language barrier is handled for you.',
    },
    {
      icon: <UserCog className="w-6 h-6" />,
      title: 'Built for Both Roles',
      description: 'Managers get planning, utilization and full control. Employees get a clean, focused view of just their projects and tasks.',
    },
  ]

  const aiPoints = [
    { title: 'Best-Person Matching', text: 'The AI weighs skills, job titles and real availability to propose the right assignee for every task.' },
    { title: 'Solid Effort Estimates', text: 'Realistic hour estimates and start dates — grounded in your team’s actual capacity.' },
    { title: 'Transparent Reasoning', text: 'Every suggestion comes with a clear explanation of why this person and why this estimate.' },
    { title: 'You Stay in Control', text: 'Approve the suggestion as-is or change any detail. The AI recommends — you decide.' },
  ]

  const stats = [
    { value: '5 min', label: 'Company setup' },
    { value: '€10', label: 'Per seat / month' },
    { value: 'Zero', label: 'Training required' },
    { value: 'Any', label: 'Language, per user' },
  ]

  const pricingIncludes = [
    'AI task assignment, estimates & reasoning',
    'Unlimited projects and tasks',
    'Utilization, working hours & absence tracking',
    'Task chat, comments & photo attachments',
    'Multilingual interface for every user',
    'Secure billing via Stripe — cancel anytime',
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-brand-950 text-brand-900 transition-colors duration-300 overflow-x-hidden">

      {/* ================= Header ================= */}
      <header
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass shadow-lg shadow-brand-800/5' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <nav className="container mx-auto px-4 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2.5 group">
              <img src="/logo.png" alt="TaskMi Logo" className="w-9 h-9 transition-transform duration-300 group-hover:scale-110" />
              <span className="font-display text-xl font-bold text-brand-800 dark:text-brand-200">TaskMi</span>
            </a>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-7">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium whitespace-nowrap text-brand-800/80 dark:text-brand-200/80 hover:text-brand-500 dark:hover:text-brand-400 transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Toggle dark mode"
                onClick={() => setDarkMode(!darkMode)}
                className="text-brand-700 dark:text-brand-300 hover:bg-brand-700/10 dark:hover:bg-brand-400/10 rounded-full"
              >
                {darkMode ? <Sun className="h-[1.2rem] w-[1.2rem]" /> : <Moon className="h-[1.2rem] w-[1.2rem]" />}
              </Button>
              <Button
                size="sm"
                className="hidden lg:inline-flex whitespace-nowrap bg-brand-700 hover:bg-brand-800 dark:bg-brand-500 dark:hover:bg-brand-400 text-white rounded-full px-5 shadow-lg shadow-brand-700/25 transition-all duration-300 hover:shadow-brand-700/40 hover:-translate-y-0.5"
                asChild
              >
                <a href="#download">Get Started</a>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Toggle menu"
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden text-brand-700 dark:text-brand-300 rounded-full"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

        </nav>
      </header>

      {/* ================= Mobile drawer ================= */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-drawer"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] lg:hidden bg-white dark:bg-brand-950 flex flex-col"
          >
            <div className="absolute inset-0 grid-pattern [mask-image:radial-gradient(ellipse_80%_60%_at_50%_20%,black,transparent)] pointer-events-none" />
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-brand-500/15 rounded-full blur-3xl pointer-events-none" />

            {/* Drawer header */}
            <div className="relative flex items-center justify-between px-4 py-3.5 container mx-auto lg:px-8">
              <a href="/" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
                <img src="/logo.png" alt="TaskMi Logo" className="w-9 h-9" />
                <span className="font-display text-xl font-bold text-brand-800 dark:text-brand-200">TaskMi</span>
              </a>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
                className="text-brand-700 dark:text-brand-300 rounded-full hover:bg-brand-700/10 dark:hover:bg-brand-400/10"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>

            {/* Drawer links */}
            <nav className="relative flex-1 flex flex-col justify-center px-8 gap-1">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  initial={{ opacity: 0, x: -32 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex items-center justify-between py-4 border-b border-brand-700/10 dark:border-brand-400/10 font-display text-2xl font-bold text-brand-800 dark:text-brand-100 hover:text-brand-500 dark:hover:text-brand-400 transition-colors"
                >
                  {link.label}
                  <ArrowRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-brand-500" />
                </motion.a>
              ))}
            </nav>

            {/* Drawer footer */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + navLinks.length * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative px-8 pb-10 pt-4"
            >
              <Button
                size="lg"
                className="w-full bg-brand-700 hover:bg-brand-800 dark:bg-brand-500 dark:hover:bg-brand-400 text-white rounded-full h-12 text-base shadow-xl shadow-brand-700/30"
                asChild
              >
                <a href="#download" onClick={() => setMenuOpen(false)} className="flex items-center justify-center">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= Hero ================= */}
      <section className="relative pt-36 pb-24 lg:pt-44 lg:pb-32">
        {/* Background */}
        <div className="absolute inset-0 grid-pattern [mask-image:radial-gradient(ellipse_70%_60%_at_50%_30%,black,transparent)]" />
        <div className="absolute top-24 -left-32 w-96 h-96 bg-brand-500/20 dark:bg-brand-500/15 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-48 -right-32 w-[28rem] h-[28rem] bg-brand-300/30 dark:bg-brand-700/25 rounded-full blur-3xl animate-float-slow" style={{ animationDelay: '-4s' }} />

        <div className="container relative mx-auto px-4 lg:px-8">
          <motion.div variants={stagger} initial="hidden" animate="visible" className="text-center max-w-4xl mx-auto">
            <motion.div variants={fadeUp} className="flex justify-center mb-6">
              <span className="border-beam inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium glass text-brand-700 dark:text-brand-300 shadow-sm">
                <Sparkles className="w-4 h-4 text-brand-500" />
                AI-Powered Task Management
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-brand-900 dark:text-white mb-6 leading-[1.08]"
            >
              The Right Person for Every Task —{' '}
              <span className="text-gradient">Chosen by AI</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-lg sm:text-xl text-brand-800/75 dark:text-brand-300/90 max-w-2xl mx-auto mb-10"
            >
              TaskMi assigns every task to the best-suited teammate, estimates the effort and explains
              its reasoning — while you keep full control. Built for teams and companies of every size.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Button
                size="lg"
                className="bg-brand-700 hover:bg-brand-800 dark:bg-brand-500 dark:hover:bg-brand-400 text-white rounded-full px-8 h-12 text-base shadow-xl shadow-brand-700/30 transition-all duration-300 hover:shadow-brand-700/50 hover:-translate-y-0.5"
                asChild
              >
                <a href="https://apps.apple.com/at/app/taskmi/id6738031950" target="_blank" rel="noopener noreferrer" className="flex items-center">
                  <Apple className="mr-2 h-5 w-5" />
                  Download on App Store
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-brand-700/30 dark:border-brand-400/30 text-brand-700 dark:text-brand-300 hover:bg-brand-700/5 dark:hover:bg-brand-400/10 rounded-full px-8 h-12 text-base transition-all duration-300 hover:-translate-y-0.5"
                asChild
              >
                <a href="#how-it-works" className="flex items-center">
                  See How It Works
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={fadeUp}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto"
            >
              {stats.map((stat) => (
                <div key={stat.label} className="glass rounded-2xl px-4 py-5 transition-transform duration-300 hover:-translate-y-1">
                  <div className="font-display text-2xl sm:text-3xl font-bold text-gradient">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-brand-800/70 dark:text-brand-300/80 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= Features ================= */}
      <section id="features" className="relative py-24 lg:py-32 bg-brand-100/50 dark:bg-brand-900/30 scroll-mt-20">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow="Why TaskMi"
            title="Plan Less. Deliver More."
            subtitle="Intelligent automation for managers, distraction-free focus for employees — one app for the whole company."
          />

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeUp}
                className="group relative p-8 rounded-3xl glass shadow-sm hover:shadow-xl hover:shadow-brand-700/10 transition-all duration-300 hover:-translate-y-1.5"
              >
                <div className="w-12 h-12 mb-5 rounded-2xl bg-gradient-to-br from-brand-700 to-brand-500 dark:from-brand-500 dark:to-brand-400 flex items-center justify-center text-white shadow-lg shadow-brand-700/25 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                  {feature.icon}
                </div>
                <h3 className="font-display text-xl font-bold text-brand-800 dark:text-brand-100 mb-2">
                  {feature.title}
                </h3>
                <p className="text-brand-800/70 dark:text-brand-300/80 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= AI Intelligence ================= */}
      <section id="intelligence" className="relative py-24 lg:py-32 overflow-hidden scroll-mt-20">
        <div className="absolute inset-0 grid-pattern [mask-image:radial-gradient(ellipse_60%_60%_at_80%_50%,black,transparent)]" />
        <div className="container relative mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            {/* Copy */}
            <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewportOnce}>
              <motion.span
                variants={fadeUp}
                className="border-beam inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-brand-700/10 dark:bg-brand-500/15 text-brand-700 dark:text-brand-300 mb-4"
              >
                <Brain className="w-4 h-4" />
                TaskMi Intelligence
              </motion.span>
              <motion.h2
                variants={fadeUp}
                className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-brand-800 dark:text-brand-100 mb-5"
              >
                Your Planning Co-Pilot, <span className="text-gradient">On Every Task</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="text-lg text-brand-800/75 dark:text-brand-300/90 mb-8">
                Create a task and the AI does the heavy lifting: it finds the best available person,
                estimates the hours and shows you exactly why — before a single hour is booked.
              </motion.p>

              <motion.ul variants={stagger} className="space-y-5">
                {aiPoints.map((point) => (
                  <motion.li key={point.title} variants={fadeUp} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-brand-500 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-brand-800 dark:text-brand-100">{point.title}</span>
                      <p className="text-brand-800/70 dark:text-brand-300/80 mt-1">{point.text}</p>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

            {/* Visual — mirrors the in-app AI Suggestion card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={viewportOnce}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-500/25 to-brand-300/20 dark:from-brand-500/20 dark:to-brand-800/30 rounded-[2.5rem] blur-2xl animate-pulse-glow" />
              <div className="relative glass rounded-[2rem] p-8 shadow-2xl shadow-brand-800/10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-700 to-brand-500 flex items-center justify-center text-white">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-brand-800 dark:text-brand-100">AI Suggestion</div>
                    <div className="text-xs text-brand-700/60 dark:text-brand-300/60">New task · Create CI/CD pipeline</div>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { w: 'w-full', text: '"Create CI/CD pipeline" — who should do it?', user: true },
                    { w: 'w-11/12', text: '✓ Assignee: D.W. — Dev, 40 free hours this week' },
                    { w: 'w-10/12', text: '✓ Estimate: 6 hours · start July 17' },
                    { w: 'w-9/12', text: '✓ Reasoning attached — approve or adjust' },
                  ].map((row, i) => (
                    <motion.div
                      key={row.text}
                      initial={{ opacity: 0, x: -16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={viewportOnce}
                      transition={{ delay: 0.3 + i * 0.2, duration: 0.5 }}
                      className={`${row.w} px-4 py-3 rounded-xl text-sm ${
                        row.user
                          ? 'bg-brand-700 dark:bg-brand-500 text-white ml-auto text-right'
                          : 'bg-brand-100 dark:bg-brand-800/60 text-brand-800 dark:text-brand-200'
                      }`}
                    >
                      {row.text}
                    </motion.div>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-2 text-xs text-brand-700/60 dark:text-brand-300/60">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500" />
                  </span>
                  AI suggestions on every new task
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= Capacity & Utilization ================= */}
      <section id="capacity" className="relative py-24 lg:py-32 bg-brand-100/50 dark:bg-brand-900/30 overflow-hidden scroll-mt-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            {/* Visual — utilization & working hours screens */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={viewportOnce}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative order-2 lg:order-1 flex justify-center"
            >
              <div className="absolute inset-8 bg-gradient-to-br from-brand-500/25 to-brand-300/20 dark:from-brand-500/20 dark:to-brand-800/30 rounded-full blur-3xl" />
              <div className="relative flex items-start gap-4 sm:gap-6">
                <div className="w-[42vw] max-w-[200px] -rotate-3 animate-float">
                  <MiniPhone
                    src="/images/working-hours.png"
                    alt="Weekly availability and absences — TaskMi app screen"
                  />
                </div>
                <div className="w-[42vw] max-w-[200px] rotate-3 translate-y-10 animate-float" style={{ animationDelay: '-3s' }}>
                  <MiniPhone
                    src="/images/utilization.png"
                    alt="Weekly utilization with free hours — TaskMi app screen"
                  />
                </div>
              </div>
            </motion.div>

            {/* Copy */}
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="order-1 lg:order-2"
            >
              <motion.span
                variants={fadeUp}
                className="border-beam inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-brand-700/10 dark:bg-brand-500/15 text-brand-700 dark:text-brand-300 mb-4"
              >
                <BarChart3 className="w-4 h-4" />
                Capacity & Utilization
              </motion.span>
              <motion.h2
                variants={fadeUp}
                className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-brand-800 dark:text-brand-100 mb-5"
              >
                Plan with <span className="text-gradient">Real Capacity</span>, Not Guesswork
              </motion.h2>
              <motion.p variants={fadeUp} className="text-lg text-brand-800/75 dark:text-brand-300/90 mb-8">
                This is where the AI's availability data comes from: every employee's working hours,
                absences and booked task hours — turned into a live utilization view.
              </motion.p>

              <motion.ul variants={stagger} className="space-y-5">
                {[
                  { title: 'Weekly Working Hours', text: 'Set each employee’s availability per weekday — down to start and end times.' },
                  { title: 'Absences Handled Automatically', text: 'Vacations and sick days reduce available capacity the moment you add them.' },
                  { title: 'Live Utilization View', text: 'Booked hours vs. free hours for every person, day by day and week by week.' },
                  { title: 'Powers Every AI Suggestion', text: 'The AI only proposes people who genuinely have the time — no overbooking.' },
                ].map((point) => (
                  <motion.li key={point.title} variants={fadeUp} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-brand-500 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-brand-800 dark:text-brand-100">{point.title}</span>
                      <p className="text-brand-800/70 dark:text-brand-300/80 mt-1">{point.text}</p>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= How It Works ================= */}
      <section id="how-it-works" className="relative py-24 lg:py-32 scroll-mt-20">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow="How It Works"
            title="From Sign-Up to AI-Planned Work in 5 Steps"
            subtitle="Set up your company, add your people — and let the AI take over the planning busywork."
          />

          <div className="max-w-6xl mx-auto">
            {/* Desktop */}
            <div className="hidden md:grid md:grid-cols-2 gap-16 items-center">
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOnce}
                className="space-y-3"
              >
                {steps.map((step, index) => (
                  <motion.button
                    key={step.title}
                    variants={fadeUp}
                    onClick={() => { setActiveStep(index); setPaused(true) }}
                    className={`w-full text-left flex items-start gap-4 p-5 rounded-2xl transition-all duration-300 ${
                      activeStep === index
                        ? 'glass shadow-lg shadow-brand-700/10 scale-[1.02]'
                        : 'opacity-60 hover:opacity-90'
                    }`}
                  >
                    <div className={`rounded-xl p-2.5 text-white shrink-0 transition-colors duration-300 ${
                      activeStep === index
                        ? 'bg-gradient-to-br from-brand-700 to-brand-500'
                        : 'bg-brand-700/50 dark:bg-brand-500/40'
                    }`}>
                      {step.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-display text-lg font-bold text-brand-800 dark:text-brand-100 mb-0.5">
                        <span className="text-brand-500 mr-2">{String(index + 1).padStart(2, '0')}</span>
                        {step.title}
                      </h3>
                      <p className="text-sm text-brand-800/70 dark:text-brand-300/80">
                        {step.description}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </motion.div>

              {/* Phone */}
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={viewportOnce}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative flex justify-center"
              >
                <div className="absolute inset-8 bg-gradient-to-br from-brand-500/25 to-brand-300/20 dark:from-brand-500/20 dark:to-brand-800/30 rounded-full blur-3xl" />
                <div className="relative z-10 w-full max-w-[280px] animate-float">
                  <PhoneFrame steps={steps} activeStep={activeStep} />
                </div>
              </motion.div>
            </div>

            {/* Mobile */}
            <div className="md:hidden">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportOnce}
                transition={{ duration: 0.6 }}
                className="flex flex-col items-center gap-6"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className="w-full glass p-6 rounded-3xl shadow-lg">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeStep}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="mb-6 text-center">
                        <div className="flex flex-col items-center gap-3 mb-2">
                          <div className="rounded-xl bg-gradient-to-br from-brand-700 to-brand-500 p-2.5 text-white">
                            {steps[activeStep].icon}
                          </div>
                          <h3 className="font-display text-xl font-bold text-brand-800 dark:text-brand-100">
                            {steps[activeStep].title}
                          </h3>
                        </div>
                        <p className="text-sm text-brand-800/70 dark:text-brand-300/80">
                          {steps[activeStep].description}
                        </p>
                      </div>
                      <div className="w-full max-w-[220px] mx-auto">
                        <PhoneFrame steps={steps} activeStep={activeStep} />
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-between w-full px-4">
                  <button
                    onClick={handlePrevStep}
                    aria-label="Previous step"
                    className="p-2.5 text-brand-700 dark:text-brand-400 glass rounded-full transition-transform active:scale-90"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex gap-2">
                    {steps.map((_, index) => (
                      <button
                        key={index}
                        aria-label={`Go to step ${index + 1}`}
                        onClick={() => { setActiveStep(index); setPaused(true) }}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === activeStep
                            ? 'w-8 bg-brand-500'
                            : 'w-2 bg-brand-500/30'
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleNextStep}
                    aria-label="Next step"
                    className="p-2.5 text-brand-700 dark:text-brand-400 glass rounded-full transition-transform active:scale-90"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= Pricing ================= */}
      <section id="pricing" className="relative py-24 lg:py-32 bg-brand-100/50 dark:bg-brand-900/30 overflow-hidden scroll-mt-20">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] bg-brand-500/10 dark:bg-brand-500/10 rounded-full blur-3xl" />
        <div className="container relative mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow="Pricing"
            title="One Plan. Everything Included."
            subtitle="No tiers, no hidden fees, no surprises — pay only for the seats you actually use."
          />

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-lg mx-auto"
          >
            <div className="relative glass rounded-[2rem] p-8 sm:p-10 shadow-2xl shadow-brand-700/10 transition-transform duration-300 hover:-translate-y-1.5">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide bg-gradient-to-r from-brand-700 to-brand-500 text-white shadow-lg shadow-brand-700/30">
                  <Sparkles className="w-3.5 h-3.5" />
                  All-Inclusive
                </span>
              </div>

              <div className="text-center mt-2 mb-8">
                <div className="flex items-baseline justify-center gap-1.5">
                  <span className="font-display text-6xl sm:text-7xl font-extrabold text-gradient">€10</span>
                </div>
                <p className="text-brand-800/70 dark:text-brand-300/80 mt-2 font-medium">
                  per seat, per month
                </p>
              </div>

              <ul className="space-y-3.5 mb-9">
                {pricingIncludes.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-500 shrink-0 mt-0.5" />
                    <span className="text-brand-800/85 dark:text-brand-200/90">{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                className="w-full bg-brand-700 hover:bg-brand-800 dark:bg-brand-500 dark:hover:bg-brand-400 text-white rounded-full h-12 text-base shadow-xl shadow-brand-700/30 transition-all duration-300 hover:shadow-brand-700/50"
                asChild
              >
                <a
                  href="https://apps.apple.com/at/app/taskmi/id6738031950"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center"
                >
                  <Apple className="mr-2 h-5 w-5" />
                  Start with TaskMi
                </a>
              </Button>
              <p className="text-center text-xs text-brand-700/60 dark:text-brand-300/60 mt-4">
                Add or remove seats anytime as your team changes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= Download / CTA ================= */}
      <section id="download" className="relative py-24 lg:py-32 overflow-hidden scroll-mt-20">
        <div className="container relative mx-auto px-4 lg:px-8">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2
              variants={fadeUp}
              className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-brand-800 dark:text-brand-100 mb-4"
            >
              Ready to Let AI Plan Your Team's Work?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-brand-800/75 dark:text-brand-300/90 mb-12">
              Download TaskMi for iOS today — Android and Web are on the way.
            </motion.p>

            <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-xl mx-auto">
              {/* App Store */}
              <div className="glass rounded-3xl p-8 flex flex-col items-center gap-5 shadow-lg transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-brand-700/10">
                <div className="p-3 bg-white rounded-2xl shadow-sm">
                  <img src="/qrios.svg" alt="App Store QR Code" className="w-24 h-24" />
                </div>
                <Button
                  size="lg"
                  className="w-full bg-brand-700 hover:bg-brand-800 dark:bg-brand-500 dark:hover:bg-brand-400 text-white rounded-full shadow-lg shadow-brand-700/25"
                  asChild
                >
                  <a
                    href="https://apps.apple.com/at/app/taskmi/id6738031950"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center"
                  >
                    <Apple className="mr-2 h-5 w-5" />
                    App Store
                  </a>
                </Button>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400">
                  <CheckCircle2 className="w-4 h-4" />
                  Available Now
                </span>
              </div>

              {/* Android & Web */}
              <div className="glass rounded-3xl p-8 flex flex-col items-center justify-center gap-5 shadow-lg opacity-80">
                <div className="w-[120px] h-[120px] rounded-2xl border-2 border-dashed border-brand-700/25 dark:border-brand-400/25 flex items-center justify-center gap-3">
                  <PlaySquare className="w-9 h-9 text-brand-700/40 dark:text-brand-400/40" />
                  <Globe className="w-9 h-9 text-brand-700/40 dark:text-brand-400/40" />
                </div>
                <Button
                  size="lg"
                  disabled
                  variant="outline"
                  className="w-full border-brand-700/30 dark:border-brand-400/30 text-brand-700/50 dark:text-brand-400/50 cursor-not-allowed rounded-full"
                >
                  Android & Web
                </Button>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700/60 dark:text-brand-400/60">
                  <CalendarClock className="w-4 h-4" />
                  Coming Soon
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= Footer ================= */}
      <footer className="relative bg-brand-800 dark:bg-brand-900 text-white">
        <div className="absolute bottom-full left-0 right-0 overflow-hidden leading-[0]">
          <svg
            viewBox="0 0 1440 100"
            className="w-full h-auto fill-brand-800 dark:fill-brand-900 block"
            preserveAspectRatio="none"
          >
            <path d="M0,64L80,58.7C160,53,320,43,480,48C640,53,800,75,960,75C1120,75,1280,53,1360,42.7L1440,32L1440,100L0,100Z" />
          </svg>
        </div>

        <div className="container mx-auto px-4 lg:px-8 pt-12 pb-8">
          <div className="grid md:grid-cols-3 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo.png" alt="TaskMi Logo" className="w-8 h-8" />
                <span className="font-display text-lg font-bold">TaskMi</span>
              </div>
              <p className="text-sm text-brand-200/80 max-w-xs">
                AI-powered task management: the right person, the right estimate, on every task —
                for teams and companies of every size.
              </p>
            </div>
            <div>
              <h4 className="font-display font-bold mb-4">Product</h4>
              <ul className="space-y-2.5 text-sm text-brand-200/80">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#intelligence" className="hover:text-white transition-colors">AI Intelligence</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#download" className="hover:text-white transition-colors">Download</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold mb-4">Company</h4>
              <ul className="space-y-2.5 text-sm text-brand-200/80">
                <li>
                  <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                </li>
                <li>
                  <a href="mailto:admin@taskmi.app" className="inline-flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="h-4 w-4" />
                    admin@taskmi.app
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 border-t border-white/10 text-center text-sm text-brand-200/70">
            &copy; {new Date().getFullYear()} TaskMi. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}

/* ---------- Compact static iPhone frame ---------- */
function MiniPhone({ src, alt }) {
  return (
    <div className="relative">
      <div className="relative z-10 bg-black rounded-[2rem] p-2 shadow-2xl">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-20 bg-black rounded-b-2xl z-20" />
        <div className="relative aspect-[9/19] rounded-[1.7rem] overflow-hidden bg-[#ededed]">
          <img src={src} alt={alt} loading="lazy" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-16 bg-white/80 rounded-full" />
      </div>
    </div>
  )
}

/* ---------- iPhone frame with animated screenshot ---------- */
function PhoneFrame({ steps, activeStep }) {
  return (
    <div className="relative">
      <div
        className="absolute inset-0 bg-gradient-to-br from-brand-700 to-brand-400 dark:from-brand-800 dark:to-brand-600 rounded-[2.5rem] opacity-90"
        style={{ transform: 'rotate(6deg)' }}
      />
      <div className="relative z-10 bg-black rounded-[2.5rem] p-2.5 shadow-2xl">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-5 w-32 bg-black rounded-b-3xl z-20" />
        <div className="relative aspect-[9/19] rounded-[2rem] overflow-hidden bg-[#ededed]">
          <AnimatePresence mode="wait">
            <motion.img
              key={steps[activeStep].image}
              src={steps[activeStep].image}
              alt={`${steps[activeStep].title} — TaskMi app screen`}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="w-full h-full object-contain"
            />
          </AnimatePresence>
        </div>
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-24 bg-white/80 rounded-full" />
      </div>
    </div>
  )
}
